import React, { useState } from "react";
import {
  Anchor,
  Badge,
  Button,
  Group,
  Loader,
  Pagination,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { Link } from "react-router-dom";
import { useTable } from "@refinedev/core";
import { IconSearch } from "@tabler/icons-react";

import type { Ingredient } from "../../types";

export function IngredientList() {
  const [searchValue, setSearchValue] = useState("");

  const {
    tableQueryResult,
    current,
    setCurrent,
    pageCount,
    setFilters,
  } = useTable<Ingredient>({
    resource: "ingredients",
    pagination: {
      current: 1,
      pageSize: 20,
    },
    filters: {
      initial: [],
    },
  });

  const isLoading = tableQueryResult.isLoading;
  const records: Ingredient[] = tableQueryResult.data?.data ?? [];

  const handleSearch = (value: string) => {
    setSearchValue(value);
    setFilters([
      {
        field: "search",
        operator: "eq",
        value: value || undefined,
      },
    ]);
    setCurrent(1);
  };

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Title order={2}>Ingredients</Title>
        <Button component={Link} to="/admin/ingredients/create">
          Add ingredient
        </Button>
      </Group>

      <TextInput
        placeholder="Search by name, category…"
        leftSection={<IconSearch size={16} />}
        value={searchValue}
        onChange={(e) => handleSearch(e.currentTarget.value)}
        style={{ maxWidth: 400 }}
      />

      {isLoading ? (
        <Group justify="center" py="xl">
          <Loader />
        </Group>
      ) : (
        <>
          <Table striped highlightOnHover withTableBorder>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Canonical name</Table.Th>
                <Table.Th>English name</Table.Th>
                <Table.Th>Japanese name</Table.Th>
                <Table.Th>Category</Table.Th>
                <Table.Th>Rarity</Table.Th>
                <Table.Th>Display</Table.Th>
                <Table.Th style={{ width: 80 }}>Actions</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {records.length === 0 ? (
                <Table.Tr>
                  <Table.Td colSpan={7}>
                    <Text c="dimmed" ta="center" py="md">
                      No ingredients found.
                    </Text>
                  </Table.Td>
                </Table.Tr>
              ) : (
                records.map((ingredient) => (
                  <Table.Tr key={ingredient.id}>
                    <Table.Td>
                      <Text fw={600}>{ingredient.canonical_name}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{ingredient.name_en ?? "—"}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{ingredient.name_jp ?? "—"}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{ingredient.category ?? "—"}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{ingredient.rarity ?? "—"}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Badge color={ingredient.display ? "green" : "gray"} variant="light">
                        {ingredient.display ? "Yes" : "No"}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Anchor component={Link} to={`/admin/ingredients/edit/${ingredient.id}`}>
                        Edit
                      </Anchor>
                    </Table.Td>
                  </Table.Tr>
                ))
              )}
            </Table.Tbody>
          </Table>

          {pageCount > 1 ? (
            <Group justify="center">
              <Pagination total={pageCount} value={current} onChange={setCurrent} />
            </Group>
          ) : null}
        </>
      )}
    </Stack>
  );
}
